import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const VentaDetails = ({ venta, onClose, onImprimirTicket, onDevolucion }) => {
  if (!venta) return null;

  return (
    <AnimatePresence>
      <motion.div
        key={venta.id}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 80, damping: 20 }}
        className="fixed inset-y-0 right-0 w-full lg:w-2/5 bg-white shadow-2xl z-50 overflow-y-auto border-l border-gray-200"
      >
        <div className="p-6">
          {/* Header del modal */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 mb-6 border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-3 rounded-xl">
                  <span className="text-white text-2xl">🧾</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                    Detalles de Venta #{venta.id}
                  </h2>
                  <p className="text-gray-600">Información completa de la transacción</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>
          </div>

          {/* Información básica */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="text-lg">📋</span>
              Información General
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-600">👤</span>
                  <span className="font-semibold text-blue-800">Cliente</span>
                </div>
                <p className="text-gray-800 font-medium">
                  {venta.cliente?.nombre || 'Consumidor final'}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-green-600">👨‍💼</span>
                  <span className="font-semibold text-green-800">Vendedor</span>
                </div>
                <p className="text-gray-800 font-medium">
                  {venta.usuario?.nombre_user || 'Sin vendedor asignado'}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-purple-600">📅</span>
                  <span className="font-semibold text-purple-800">Fecha</span>
                </div>
                <p className="text-gray-800 font-medium">
                  {new Date(venta.createdAt).toLocaleDateString('es-AR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-yellow-600">💰</span>
                  <span className="font-semibold text-yellow-800">Total</span>
                </div>
                <p className="text-gray-800 font-bold text-lg">
                  ${parseFloat(venta.total).toFixed(2)}
                </p>
                {parseFloat(venta.descuento) > 0 && (
                  <p className="text-green-600 text-sm font-medium">
                    (Descuento: ${parseFloat(venta.descuento).toFixed(2)})
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Información de Pago */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="text-lg">💳</span>
              Información de Pago
            </h3>
            
            {/* Verificar si tiene pagos múltiples */}
            {venta.tiene_pagos_multiples && venta.detallesPago && venta.detallesPago.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3 p-3 bg-blue-50 rounded-lg">
                  <span className="text-blue-600">🔀</span>
                  <span className="font-semibold text-blue-800">
                    Pagos Múltiples ({venta.detallesPago.length} formas)
                  </span>
                </div>
                
                {venta.detallesPago.map((pago, index) => (
                  <div key={index} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-bold">
                          #{index + 1}
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            {/* Iconos según la forma de pago */}
                            {pago.forma_pago === 'efectivo' && <span>💵</span>}
                            {pago.forma_pago === 'transferencia' && <span>🏦</span>}
                            {pago.forma_pago === 'tarjeta' && <span>💳</span>}
                            {pago.forma_pago === 'otros' && <span>🏷️</span>}
                            {pago.forma_pago === 'cuentaCorriente' && <span>📋</span>}
                            
                            <span className="font-semibold text-gray-800 capitalize">
                              {pago.forma_pago.replace('_', ' ').replace('cuentaCorriente', 'Cuenta Corriente')}
                            </span>
                          </div>
                          
                          {/* Referencia si existe */}
                          {pago.referencia && (
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Ref:</span> {pago.referencia}
                            </p>
                          )}
                          
                          {/* Observaciones si existen */}
                          {pago.observaciones && (
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Obs:</span> {pago.observaciones}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="bg-green-100 text-green-800 px-3 py-2 rounded-lg">
                          <span className="font-bold text-lg">
                            ${parseFloat(pago.monto).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Total de verificación */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-green-800">
                      ✅ Total Verificado:
                    </span>
                    <span className="font-bold text-green-800 text-lg">
                      ${venta.detallesPago.reduce((sum, pago) => sum + parseFloat(pago.monto), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* Pago simple (sistema anterior) */
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Icono según la forma de pago */}
                    {venta.detalles_pago === 'efectivo' && <span className="text-2xl">💵</span>}
                    {venta.detalles_pago === 'transferencia' && <span className="text-2xl">🏦</span>}
                    {venta.detalles_pago === 'tarjeta' && <span className="text-2xl">💳</span>}
                    {venta.detalles_pago === 'otros' && <span className="text-2xl">🏷️</span>}
                    {venta.detalles_pago === 'cuentaCorriente' && <span className="text-2xl">📋</span>}
                    
                    <div>
                      <span className="font-semibold text-blue-800 text-lg capitalize">
                        {venta.detalles_pago?.replace('_', ' ').replace('cuentaCorriente', 'Cuenta Corriente') || 'No especificado'}
                      </span>
                      <p className="text-blue-600 text-sm">Pago único</p>
                    </div>
                  </div>
                  
                  <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg">
                    <span className="font-bold text-lg">
                      ${(parseFloat(venta.total) - parseFloat(venta.descuento || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Detalles del pedido */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="text-lg">📦</span>
              Productos Vendidos ({venta.detalles_venta?.length || 0})
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {venta.detalles_venta?.map((detalle, index) => (
                <div key={detalle.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:bg-gray-100 transition-all duration-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-bold">
                          #{index + 1}
                        </span>
                        <h4 className="font-bold text-gray-800">
                          {detalle.producto?.nombre_producto || 'Producto sin nombre'}
                        </h4>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">📊 Cantidad:</span>
                          <span className="font-bold text-gray-800 ml-2">{detalle.cantidad}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">💵 Precio:</span>
                          <span className="font-bold text-gray-800 ml-2">${parseFloat(detalle.precio).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-lg">
                        <span className="font-bold">
                          ${(parseFloat(detalle.cantidad) * parseFloat(detalle.precio)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => onImprimirTicket(venta)}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <span className="text-lg">🖨️</span>
              Imprimir Ticket
            </button>
            <button
              onClick={() => onDevolucion(venta)}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <span className="text-lg">↩️</span>
              Procesar Devolución
            </button>
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <span className="text-lg">❌</span>
              Cerrar
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VentaDetails;
